import type { ErrorEvent } from "@sentry/nextjs";

const DROP_ERROR_TYPE_TAGS = new Set<string>(["invalid_booking_not_found"]);

const DROP_EXCEPTION_TYPES = new Set<string>(["AbortError"]);

// Only external noise we don't own — flow errors we control are handled at the
// source (useDigiLockerCallback), not matched here.
const DROP_MESSAGE_PATTERNS = [
  "Java object is gone",
  "Object Not Found Matching Id",
  "MetaMask",
  "clearClarity",
  "EADDRINUSE",
  "The user aborted a request",
] as const;

const NETWORK_WARNING_PATTERNS = [
  "Failed to fetch",
  "Load failed",
  "status code 502",
  "network error",
  "Upload failed",
  "Failed to upload file to S3",
  "Failed to load Razorpay script",
] as const;

const getEventSignature = (event: ErrorEvent): string => {
  const exceptionText = (event.exception?.values ?? [])
    .map((value) => `${value.type ?? ""}: ${value.value ?? ""}`)
    .join(" ");
  const message = typeof event.message === "string" ? event.message : "";
  return `${message} ${exceptionText}`;
};

export const filterSentryEvent = (event: ErrorEvent): ErrorEvent | null => {
  const errorTypeTag = event.tags?.errorType;
  if (
    typeof errorTypeTag === "string" &&
    DROP_ERROR_TYPE_TAGS.has(errorTypeTag)
  ) {
    return null;
  }

  const hasDroppedExceptionType = (event.exception?.values ?? []).some(
    (value) => !!value.type && DROP_EXCEPTION_TYPES.has(value.type),
  );
  if (hasDroppedExceptionType) {
    return null;
  }

  const signature = getEventSignature(event);
  if (DROP_MESSAGE_PATTERNS.some((pattern) => signature.includes(pattern))) {
    return null;
  }

  if (NETWORK_WARNING_PATTERNS.some((pattern) => signature.includes(pattern))) {
    event.level = "warning";
  }

  return event;
};
